import { ILogger, LogLevel } from '@dirootie-logging/logging-types';
import { AxiosHttpErrorCodes, ErrorHandler } from '@dirootie-http/http-axios';
import { HttpStatusCode } from '@dirootie-http/http-types';
import {
  IUserNotificationService,
  UserNotification,
  serverConnectionFailedNotification,
} from '@dirootie-web/components-types';
import { IViewNavigator } from '@dirootie-web/web-apps-types';
import { AxiosError } from 'axios';

type Errors = {
  [key: string]: {
    error: string;
    message: string;
  }[];
};

type BackEndErrorResponse = {
  detail: string;
  extensions: {
    errors: Errors;
  };
  instance: any | null;
  status: number;
  title: string;
  type: string;
};

export class ErrorHandlersFactory {
  public static inject = [
    'logger',
    'userNotificationService',
    'navigator',
  ] as const;
  constructor(
    public logger: ILogger,
    public userNotificationService: IUserNotificationService,
    public navigator: IViewNavigator
  ) {}

  notifyErrorHandler(notification: UserNotification) {
    return (error: AxiosError) => {
      this.userNotificationService.error(notification);
      return Promise.resolve();
    };
  }

  loggerErrorHandler = (
    errorType: AxiosHttpErrorCodes,
    level: LogLevel = 'WARN'
  ) => {
    return (error: AxiosError) => {
      this.logger.error(
        `[AxiosHttpClient:errorInterceptor]: error type is ${errorType}.`,
        error
      );
      return Promise.reject(error);
    };
  };

  getFirstError = (errors: any) => {
    for (const key in errors) {
      if (errors[key].length > 0) {
        return errors[key][0];
      }
    }
  };

  mapError = (error: BackEndErrorResponse) => {
    switch (error.status) {
      case 400:
        this.userNotificationService.error({
          title: error.title + error.detail,
        });
        return error;
      case 401:
        this.userNotificationService.error({
          title: 'You are not authorized to access this resource.',
        });
        return error;
      case 403:
        this.userNotificationService.error({
          title: 'You are forbidden to access this resource.',
        });
        return error;
      case 404:
        this.userNotificationService.error({
          title: 'This resource was not found.',
        });
        return error;
      case 405:
        this.userNotificationService.error({
          title: 'Method not allowed.',
        });
        return error;
      case 422: {
        const firstError = this.getFirstError(error.extensions.errors);
        this.userNotificationService.error({ title: firstError.message });
        return error;
      }
      default: {
        this.userNotificationService.error({
          title: 'An error occurred while processing your request.',
        });
        return error;
      }
    }
  };

  navigateErrorHandler = {
    ERR_NETWORK: (e: any) => {
      this.userNotificationService.error(serverConnectionFailedNotification);
      return Promise.reject(e);
    },
    NOT_FOUND: (e: any) => {
      const errorMapped = this.mapError(e.response!.data);
      return Promise.reject(errorMapped);
    },
    FORBIDDEN: (e: any) => {
      const errorMapped = this.mapError(e.response!.data);
      return Promise.reject(errorMapped);
    },
    UNAUTHORIZED: (e: any) => {
      const errorMapped = this.mapError(e.response!.data);
      return Promise.reject(errorMapped);
    },
    BAD_REQUEST: (e: any) => {
      const errorMapped = this.mapError(e.response!.data);
      return Promise.reject(errorMapped);
    },
    METHOD_NOT_ALLOWED: (e: any) => {
      const errorMapped = this.mapError(e.response.data);
      return Promise.reject(errorMapped);
    },
    UNPROCESSABLE_ENTITY: (e: any) => {
      const errorMapped = this.mapError(e.response.data);
      return Promise.reject(errorMapped);
    },
  };

  errorHandlersDefault: [AxiosHttpErrorCodes, ErrorHandler][] = [
    ['ERR_NETWORK', this.navigateErrorHandler.ERR_NETWORK],
    ['UNDEFINED_ERROR', this.loggerErrorHandler('UNDEFINED_ERROR')],
    ['UNDEFINED_CODE', this.loggerErrorHandler('UNDEFINED_CODE')],
    ['UNDEFINED_STATUS', this.loggerErrorHandler('UNDEFINED_STATUS')],
    [HttpStatusCode.BAD_REQUEST, this.navigateErrorHandler.BAD_REQUEST],
    [HttpStatusCode.UNAUTHORIZED, this.navigateErrorHandler.UNAUTHORIZED],
    [HttpStatusCode.FORBIDDEN, this.navigateErrorHandler.FORBIDDEN],
    [HttpStatusCode.NOT_FOUND, this.navigateErrorHandler.NOT_FOUND],
    [
      HttpStatusCode.METHOD_NOT_ALLOWED,
      this.navigateErrorHandler.METHOD_NOT_ALLOWED,
    ],
    [
      HttpStatusCode.UNPROCESSABLE_ENTITY,
      this.navigateErrorHandler.UNPROCESSABLE_ENTITY,
    ],
  ];
  Create() {
    return this.errorHandlersDefault;
  }
}
